import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: `By accessing or using LocalContact ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. We reserve the right to update these terms at any time, with notice provided via email or within the platform.`,
  },
  {
    title: '2. Description of Service',
    body: `LocalContact is a live search tool that identifies decision makers at local businesses using name, address, and job title inputs. Each search consumes credits from your account balance. Results are returned in real time and are not stored in a permanent database.`,
  },
  {
    title: '3. Credit-Based Access',
    body: `Access to search features requires a valid credit balance. Credits are purchased in packs and expire 60 days from the date of purchase. Unused credits are forfeited at expiry. Credits are consumed at the time of search. If a search returns zero results, no credit is charged.`,
  },
  {
    title: '4. Acceptable Use',
    body: `You agree to use the Service only for lawful B2B prospecting and outreach purposes. You must not: (a) use the Service to send unsolicited bulk communications; (b) resell, redistribute, or share access to the Service; (c) attempt to reverse engineer, scrape, or systematically extract data from the platform beyond your purchased usage; (d) use the Service to harass, stalk, or target individuals for non-commercial purposes.`,
  },
  {
    title: '5. Data and Privacy',
    body: `Contact data retrieved through the Service is subject to our Privacy Policy. We automatically delete all retrieved contact data from our systems 30 days after each search. You are responsible for any data you export and store externally, and must handle it in compliance with applicable data protection laws including GDPR and CCPA.`,
  },
  {
    title: '6. Intellectual Property',
    body: `All platform design, code, trademarks, and branding associated with LocalContact are the property of the Company. You may not use our name, logo, or branding without prior written consent.`,
  },
  {
    title: '7. Disclaimer of Warranties',
    body: `The Service is provided "as is" without warranty of any kind. We do not guarantee the accuracy, completeness, or availability of search results. Contact information is retrieved in real time and may contain errors or omissions. We are not liable for any actions taken based on data retrieved through the Service.`,
  },
  {
    title: '8. Limitation of Liability',
    body: `To the maximum extent permitted by law, LocalContact shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of revenue or data, arising from your use of the Service. Our total liability to you for any claim shall not exceed the amount you paid us in the 30 days preceding the claim.`,
  },
  {
    title: '9. Termination',
    body: `We reserve the right to suspend or terminate your account at any time for violation of these Terms, suspected fraud, or misuse of the Service. Upon termination, unused credits are forfeited and non-refundable unless otherwise required by applicable law.`,
  },
  {
    title: '10. Governing Law',
    body: `These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which the Company is incorporated, without regard to conflict of law principles. Any disputes shall be subject to the exclusive jurisdiction of the courts in that jurisdiction.`,
  },
  {
    title: '11. Contact',
    body: `If you have any questions about these Terms, please contact us at support@localcontact.ai.`,
  },
];

export default function TermsOfService() {
  useDocumentTitle('Terms of Service');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* minimal nav */}
      <header className="border-b border-slate-100">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="LocalContact" className="h-7 w-auto object-contain" />
          </Link>
          <Link to="/register" className="text-[13px] font-bold text-slate-600 hover:text-slate-900 transition-colors">
            Back to sign up
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-16 pb-24">
        {/* header */}
        <div className="mb-12">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-[-0.035em] text-slate-900 mb-4">Terms of Service</h1>
          <p className="text-[14px] text-slate-400 font-medium">Last updated: June 2025</p>
        </div>

        <div className="prose prose-slate max-w-none space-y-10">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h2 className="text-[17px] font-bold text-slate-900 mb-3">{section.title}</h2>
              <p className="text-[15px] text-slate-600 leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-slate-100 flex flex-wrap gap-4 text-[13px] text-slate-400">
          <Link to="/privacy" className="hover:text-slate-700 transition-colors font-medium">Privacy Policy</Link>
          <span>·</span>
          <Link to="/" className="hover:text-slate-700 transition-colors font-medium">Back to home</Link>
        </div>
      </div>
    </div>
  );
}
